import { Table, type TableProps } from 'antd'
import { type FC, type ComponentPropsWithoutRef, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import { useCustomKeySettingsQuery } from '@/features/custom-key-settings/hooks'
import { useAntdTableSort } from '@/features/shared/hooks'
import { type Sorter } from '@/features/shared/models'
import { type ShipmentOrdersListExEntity } from '@/libs/api'
import { type ShipmentDeliveryStatusType } from '@/libs/api/types'

import {
  buildBundleSelectionStates,
  type ShipmentOrdersSearch
} from '../../-models'
import { type ShipmentOrdersFormValues } from '../../-models/shipment-orders-form-values'

import { createShipmentOrdersTableColumns } from './shipment-orders-table.columns'
import { LEFT_FIXED_WIDTH } from './shipment-orders-table.constants'

type Props = ComponentPropsWithoutRef<'div'> & {
  tenantKey: string
  /** 親でピン留め合成済みの dataSource（選択済みだが現ページに無い行 + 現ページの行） */
  shipmentOrders: ShipmentOrdersListExEntity[]
  search: ShipmentOrdersSearch
  onChangeSort: (sorter: Sorter) => void
  isEditing: boolean
  selectedRowKeys: React.Key[]
  onSelectionChange: (
    keys: React.Key[],
    rows: (ShipmentOrdersListExEntity | undefined)[]
  ) => void
  /** ページング・絞り込みを跨いで選択を維持するため先頭にピン留め表示している行の id 集合 */
  pinnedRowIds: Set<number>
  onStatusChange?: (
    id: number,
    deliveryStatus: ShipmentDeliveryStatusType
  ) => Promise<void>
  statusEditVariant?: 'clickable-tag' | 'tag-with-edit-icon'
  onClickOrderNo?: (record: ShipmentOrdersListExEntity) => void
  /** まとめキー左のチェックで、同じ束ねの依頼をまとめて行選択する（未指定ならチェックを出さない） */
  onToggleBundleSelection?: (record: ShipmentOrdersListExEntity) => void
  /**
   * sticky な列ヘッダをスクロール領域上端から何 px 下に貼り付けるか。
   * 選択中の一括操作バーが上に固定される分をここでずらす。
   * @default 0
   */
  stickyOffsetHeader?: number
}

export const ShipmentOrdersTableBody: FC<Props> = ({
  tenantKey,
  shipmentOrders,
  search,
  onChangeSort,
  isEditing,
  selectedRowKeys,
  onSelectionChange,
  pinnedRowIds,
  onStatusChange,
  statusEditVariant,
  onClickOrderNo,
  onToggleBundleSelection,
  stickyOffsetHeader = 0
}) => {
  const handleTableChange =
    useAntdTableSort<ShipmentOrdersListExEntity>(onChangeSort)

  const sorter = useMemo<Sorter>(() => {
    return {
      sort: search.sort,
      order: search.order
    }
  }, [search.sort, search.order])

  const { control, register } = useFormContext<ShipmentOrdersFormValues>()

  // 編集中のみ有効な、選択順を保った安定 index マップ（dataSource 上の位置には依存しない）。
  // ピン留めで dataSource の並びが動的に変わっても、同じ行が常に同じフォーム配列 index に
  // 対応するようにするための仕組み
  const editableRowIndexById = useMemo(
    () =>
      isEditing
        ? new Map(selectedRowKeys.map((key, index) => [Number(key), index]))
        : new Map<number, number>(),
    [isEditing, selectedRowKeys]
  )

  // まとめキー左のチェックの表示状態。画面に出ている行（ピン留め済みの選択行を含む）を
  // 束ね単位に集計して求める。押下時の分岐（全選択 or 解除）も同じ集計を使う
  const bundleSelection = useMemo(() => {
    if (!onToggleBundleSelection) return undefined
    return {
      stateByBundleId: buildBundleSelectionStates(
        shipmentOrders,
        new Set(selectedRowKeys.map(Number))
      ),
      disabled: isEditing,
      onToggle: onToggleBundleSelection
    }
  }, [onToggleBundleSelection, shipmentOrders, selectedRowKeys, isEditing])

  // VOC_PJ-960 #4487: 拠点のカスタムキー設定（列ヘッダーのラベル解決 + 表示スロット判定）
  const { data: customKeySettings } = useCustomKeySettingsQuery(
    tenantKey,
    search.dcId
  )

  const columns = useMemo(
    () =>
      createShipmentOrdersTableColumns(
        tenantKey,
        sorter,
        editableRowIndexById,
        control,
        register,
        onStatusChange,
        statusEditVariant,
        onClickOrderNo,
        bundleSelection,
        customKeySettings
      ),
    [
      tenantKey,
      sorter,
      editableRowIndexById,
      control,
      register,
      onStatusChange,
      statusEditVariant,
      onClickOrderNo,
      bundleSelection,
      customKeySettings
    ]
  )

  const rowSelection: TableProps<ShipmentOrdersListExEntity>['rowSelection'] = {
    selectedRowKeys,
    // ページング・絞り込みを跨いで選択済み行を保持するため、現在の dataSource に無い
    // キーも AntD 内部キャッシュ (preserveRecordsRef) に残す
    preserveSelectedRowKeys: true,
    onChange: (keys, rows) => onSelectionChange(keys, rows),
    getCheckboxProps: (record: ShipmentOrdersListExEntity) => ({
      disabled: isEditing,
      name: record.orderNo
    })
  }

  return (
    <Table
      size="small"
      rowKey="id"
      dataSource={shipmentOrders}
      pagination={false}
      sticky={{
        offsetHeader: stickyOffsetHeader
      }}
      columns={columns}
      scroll={{
        x: LEFT_FIXED_WIDTH
      }}
      showSorterTooltip={false}
      onChange={handleTableChange}
      rowSelection={rowSelection}
      // ピン留め行は選択済み行でもあり AntD 標準の選択行背景 (cell 単位) が優先されて
      // 隠れてしまうため、セルに対して !important で上書きし常に視認できるようにする
      rowClassName={(record) =>
        pinnedRowIds.has(record.id) ? '[&_.ant-table-cell]:!bg-amber-50' : ''
      }
    />
  )
}
